import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {ComponentProps, FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {Z_INDEXES} from '../../../constants/layers';
import {SPACING} from '../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../ButtonsDeprecated/UnstyledButton';
import {ContentOpener} from '../ContentPopper/ContentOpener';
import {ContentPopperBaseProps} from '../ContentPopper/ContentPopper';
import {FooterWrapper} from '../ContentPopper/ContentPopperButtons/ContentPopperButtons';
import {ContentPopperHeader} from '../ContentPopper/ContentPopperHeader';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';
import {Modal, ModalHeader} from '../Modal/Modal';

type ContentAlignment =
  | 'center'
  | 'baseline'
  | 'end'
  | 'start'
  | 'flex-start'
  | 'flex-end'
  | 'stretch';

export interface ModalDialogProps
  extends Omit<ContentPopperBaseProps, 'hideCloseButton'> {
  contentAlignment?: ContentAlignment;
  contentFitWidth?: boolean;
  contentTransparent?: boolean;
  backgroundBlur?: boolean;
  fixedSmallWidth?: boolean;
  preventAnimation?: boolean;
  onUnmount?: VoidFunction;
  zIndex?: Z_INDEXES;
  preventScrollLock?: boolean;
}

/**
 * Modal Dialog Figma Design
 * https://www.figma.com/design/egsGhkDywC684XKHFOx2Ea/DT-5202---UI-Modal-Alignment?node-id=11013-19705&m=dev
 */
export const ModalDialog: FC<ModalDialogProps> = ({
  'data-testid': dataTestId = 'modalDialog',
  headerContent,
  bodyContent,
  footerContent,
  openerContent,
  onOpenerClick,
  open,
  isActive,
  onClose,
  onGoBack,
  contentAlignment,
  contentFitWidth,
  contentTransparent = false,
  renderInPortal,
  backgroundBlur,
  fixedSmallWidth,
  preventAnimation = false,
  onUnmount,
  zIndex,
  preventScrollLock,
  ...rest
}) => {
  const handleClose = (
    e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    onClose?.();
  };

  return (
    <>
      {openerContent ? (
        <ContentOpener
          isOpen={open}
          isActive={isActive}
          openerType={null}
          data-testid={dataTestId ? `${dataTestId}Opener` : 'opener'}
          onClick={onOpenerClick}
        >
          {openerContent}
        </ContentOpener>
      ) : null}

      <StyledModal
        {...rest}
        preventScrollLock={preventScrollLock}
        backgroundBlur={backgroundBlur}
        renderInPortal={renderInPortal}
        onClose={onClose}
        onModalClick={handleClose}
        data-testid={dataTestId}
        open={open}
        onUnmount={onUnmount}
        preventAnimation={preventAnimation}
        zIndex={zIndex}
        bodyContent={
          <ModalDialogContent
            id="modalContent"
            data-fixed-small-width={Boolean(
              !contentFitWidth && fixedSmallWidth,
            )}
            data-testid={`${dataTestId}Body`}
            data-content-fit-width={Boolean(contentFitWidth)}
            $contentAlignment={contentAlignment || 'center'}
            $contentTransparent={contentTransparent}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            {headerContent && (
              <ContentPopperHeader
                data-testid={dataTestId}
                onGoBack={onGoBack}
                onClose={handleClose}
              >
                {headerContent}
              </ContentPopperHeader>
            )}
            <ModalDialogBodyContent
              $enableTopDivider={Boolean(headerContent)}
              $enableBottomDivider={Boolean(footerContent)}
            >
              {bodyContent}
            </ModalDialogBodyContent>
            {footerContent && <FooterWrapper>{footerContent}</FooterWrapper>}
          </ModalDialogContent>
        }
      />
    </>
  );
};

export const ModalDialogCloseButton: React.FC<
  Pick<ComponentProps<typeof Icon>, 'size' | 'fill' | 'className'> & {
    'data-testid'?: string;
    className?: string;
    onClick?: VoidFunction | ((e: React.MouseEvent<HTMLButtonElement>) => void);
  }
> = ({
  className,
  'data-testid': dataTestId = 'modalDialogClose',
  onClick,
  fill,
  size = 'S',
}) => {
  const {t} = useTranslation();
  return (
    <StyledCloseButton
      className={className}
      aria-label={t('Close')}
      onClick={onClick}
      data-testid={dataTestId}
    >
      <Icon asset={GenericIconName.Close} size={size} fill={fill} />
    </StyledCloseButton>
  );
};

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;

  ${ModalHeader} {
    display: none;
  }
`;

export const ModalDialogHeaderContent = styled.div`
  display: flex;
  flex-direction: row;
  z-index: 1;
  justify-content: center;

  padding: ${SPACING['Y.Lg']};
  padding-left: ${SPACING['X.Xl4']};

  gap: ${SPACING['X.Lg']};

  > div {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  }

  &,
  & * {
    font-size: ${FONT_SIZES.px18};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};
    line-height: 1.4;
  }
`;

const StyledCloseButton = styled(UnstyledButton)`
  z-index: 1;
  border: none;
  padding: 2px;
  box-sizing: content-box;
`;

export const ModalDialogContent = styled.div<{
  $contentAlignment: ContentAlignment;
  $contentTransparent: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch; /* iOS Momentum Scroll */
  box-sizing: border-box;
  border-radius: 16px;

  width: 100%;

  ${tabletAndUpLinaria(`
    width: 600px;
  `)}

  &[data-content-fit-width='true'] {
    width: auto;
  }

  // Need to be VH, since using % will cause a reduced height when the modal is rendered within a smaller container, like the the mini ADP
  max-height: 80vh;
  background-clip: padding-box;
  align-self: ${({$contentAlignment}) => $contentAlignment};
  position: relative;
  overflow: auto;
  background: ${({$contentTransparent}) =>
    $contentTransparent ? 'transparent' : COLORS.White};
  box-shadow: 0 0 75px 0 rgba(0, 0, 0, 0.1), 0 2px 30px 0 rgba(0, 0, 0, 0.02);
  z-index: 1;

  &[data-fixed-small-width='true'] {
    width: 340px;
    min-width: unset;
  }
`;

export const ModalDialogBodyContent = styled.div<{
  $enableTopDivider: boolean;
  $enableBottomDivider: boolean;
}>`
  border-top: ${props =>
    props.$enableTopDivider
      ? `1px solid  ${COLORS['Outline.Grey']}`
      : 'initial'};
  border-bottom: ${props =>
    props.$enableBottomDivider
      ? `1px solid ${COLORS['Outline.Grey']}`
      : 'initial'};
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
  overflow-y: auto;

  & > * {
    font-size: ${FONT_SIZES.px14};
    font-weight: ${FONT_WEIGHTS.mediumBold};
    line-height: 1.42;
  }
`;
